'use client'

import { confirm } from "components/Confirmation";
import GeneralForm from "components/GeneralForm";
import { hideLoading, showLoading } from "components/Loading";
import TitleFormWithEdit from "components/TitleFormWithEdit";
import { validate } from "components/validateGeneralForm";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { Button, Card, CardBody, Container, Input, Table } from "reactstrap";
import comboService from "services/ComboService";
import loteService from "services/LoteService";
import { erroInesperado, formatDate } from "services/Utils";

function formatarDataHora(value) {
  if (!value) return ''
  const d = new Date(value)
  return formatDate(d) + ' ' + d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })
}

function mostrarErro(ex) {
  if (!!ex?.response?.data?.message)
    toast(ex.response.data.message)
  else
    erroInesperado()
}

export default function FormLote() {
  const [data, setData] = useState({})
  const [municipios, setMunicipios] = useState([])
  const [fiscaisCombo, setFiscaisCombo] = useState([])
  const [idFiscalAdicionar, setIdFiscalAdicionar] = useState('')
  const [edit, setEdit] = useState(false)
  const params = useParams()
  const navigate = useNavigate()

  const id = parseInt(params.id)
  const novo = (0 === id)

  useEffect(() => {
    if (!!id) {
      loteService.getById(id).then(res => {
        if (res.success) {
          setData(res.data)
        }
      })
    } else {
      setData({})
    }
  }, [id])

  useEffect(() => {
    if (novo) {
      setEdit(true)
    }
  }, [novo])

  useEffect(() => {
    comboService.municipio().then(res => {
      if (res.success) {
        setMunicipios(res.data)
      } else {
        setMunicipios([])
      }
    })

    comboService.fiscal().then(res => {
      if (res.success) {
        setFiscaisCombo(res.data)
      } else {
        setFiscaisCombo([])
      }
    })
  }, [])

  const formConfig = useMemo(() => {
    return [
      [
        { name: "nome", label: 'Nome', type: "text", required: true, colspan: 6 },
        { name: "regiao", label: 'Região', type: "text", required: true, colspan: 6 },
        { name: "idMunicipio", label: 'Município', type: "combo", options: municipios.map(el => ({ value: el.id, label: `${el.nome} / ${el.uf}` })), showDefault: true, required: true, colspan: 4 },
        { name: "observacoes", label: 'Observações', type: "textarea", required: false, colspan: 12, style: { height: 130 } },
        { name: "visto", type: "view", disabled: true, colspan: 12, component: () => <h2 className="mt-3">Visto</h2> },
        { name: "vistoEngenheiro", label: 'Engenheiro', type: "text", required: true, colspan: 12 },
        { name: "vistoDepto", label: 'Departamento', type: "text", required: true, colspan: 6 },
        { name: "vistoCodDepto", label: 'Cód. Departamento', type: "text", required: true, colspan: 6 },
      ]
    ]
  }, [municipios])

  // fiscais do lote (SDD 036): atuais = sem termino; encerrados = historico
  const fiscaisAtuais = useMemo(() => (data.fiscais || []).filter(f => !f.termino), [data.fiscais])
  const fiscaisEncerrados = useMemo(() => (data.fiscais || []).filter(f => !!f.termino), [data.fiscais])
  const opcoesFiscal = useMemo(
    () => fiscaisCombo.filter(f => !fiscaisAtuais.some(a => a.idFiscal == f.id)),
    [fiscaisCombo, fiscaisAtuais]
  )

  const handleChange = (name, value) => {
    setData({ ...data, [name]: value })
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    const errors = validate(formConfig, data)
    if (errors.length > 0) {
      setData({ ...data, errors })
      return
    }

    // fiscais sao gerenciados no card proprio, nao vao no payload do lote
    const { fiscais, idFiscal, fiscal, errors: _errors, ...payload } = data

    try {
      if (novo) {
        await loteService.create(payload)
      } else {
        await loteService.update(id, payload)
      }
      toast('Registro salvo com sucesso!')
      navigate('/admin/lotes')
    } catch (ex) {
      erroInesperado()
    }

  }

  // recarrega so a lista de fiscais, preservando edicoes nao salvas do formulario
  const recarregarFiscais = async () => {
    const res = await loteService.getById(id)
    if (res.success) {
      setData(d => ({ ...d, fiscais: res.data.fiscais }))
    }
  }

  const handleAdicionarFiscal = async () => {
    if (!idFiscalAdicionar) {
      toast('Selecione o fiscal')
      return
    }

    try {
      showLoading()
      await loteService.adicionarFiscal(id, parseInt(idFiscalAdicionar))
      setIdFiscalAdicionar('')
      await recarregarFiscais()
      hideLoading()
      toast('Fiscal adicionado')
    } catch (ex) {
      hideLoading()
      mostrarErro(ex)
    }
  }

  const handleRemoverFiscal = (item) => {
    confirm({
      header: "Remover fiscal",
      text: `Tem certeza que deseja remover ${item.nome} dos fiscais deste lote? O registro ficará no histórico.`,
      icon: "bi-exclamation-triangle",
      onConfirm: async () => {
        try {
          showLoading()
          await loteService.removerFiscal(id, item.id)
          await recarregarFiscais()
          hideLoading()
          toast('Fiscal removido')
        } catch (ex) {
          hideLoading()
          mostrarErro(ex)
        }
      }
    })
  }

  return (
    <Container fluid>
      <TitleFormWithEdit title="Lotes" edit={edit} onChangeEdit={setEdit} showButton={!novo} />
      <Card>
        <CardBody>
          <form onSubmit={handleSubmit}>
            <GeneralForm
              config={formConfig}
              data={data}
              onChange={handleChange}
              disabled={!edit}
            />
            {edit && (
              <Button type="submit" color="primary">Salvar</Button>
            )}
          </form>
        </CardBody>
      </Card>

      <Card className="mt-4 mb-4">
        <CardBody>
          <h2 className="mb-3">Fiscais</h2>

          {novo ? (
            <div className="text-muted">Salve o lote e entre nele novamente para informar os fiscais.</div>
          ) : (
            <>
              <h3 className="mb-2">Fiscais atuais</h3>
              <Table className="align-items-center table-flush" responsive>
                <thead className="thead-light">
                  <tr>
                    <th scope="col">Nome</th>
                    <th scope="col">Início</th>
                    {edit && <th scope="col" />}
                  </tr>
                </thead>
                <tbody>
                  {fiscaisAtuais.map(el => (
                    <tr key={el.id}>
                      <td>{el.nome}</td>
                      <td>{formatarDataHora(el.inicio)}</td>
                      {edit && (
                        <td className="text-right">
                          <Button color="danger" size="sm" onClick={() => handleRemoverFiscal(el)}>Remover</Button>
                        </td>
                      )}
                    </tr>
                  ))}
                  {fiscaisAtuais.length === 0 && (
                    <tr>
                      <td colSpan={edit ? 3 : 2} className="text-center">Nenhum fiscal atual</td>
                    </tr>
                  )}
                </tbody>
              </Table>

              {edit && (
                <div className="mt-3">
                  <label>Adicionar fiscal</label>
                  <div className="d-flex align-items-center" style={{ gap: 10 }}>
                    <Input
                      type="select"
                      value={idFiscalAdicionar}
                      onChange={e => setIdFiscalAdicionar(e.target.value)}
                      style={{ maxWidth: 400 }}
                    >
                      <option value="">Selecione</option>
                      {opcoesFiscal.map(f => (
                        <option key={f.id} value={f.id}>{f.nome}</option>
                      ))}
                    </Input>
                    <Button color="primary" onClick={handleAdicionarFiscal} disabled={!idFiscalAdicionar}>Adicionar</Button>
                  </div>
                </div>
              )}

              <h3 className="mt-4 mb-2">Histórico</h3>
              <Table className="align-items-center table-flush" responsive>
                <thead className="thead-light">
                  <tr>
                    <th scope="col">Nome</th>
                    <th scope="col">Início</th>
                    <th scope="col">Término</th>
                  </tr>
                </thead>
                <tbody>
                  {fiscaisEncerrados.map(el => (
                    <tr key={el.id}>
                      <td>{el.nome}</td>
                      <td>{formatarDataHora(el.inicio)}</td>
                      <td>{formatarDataHora(el.termino)}</td>
                    </tr>
                  ))}
                  {fiscaisEncerrados.length === 0 && (
                    <tr>
                      <td colSpan={3} className="text-center">Nenhum registro</td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </>
          )}
        </CardBody>
      </Card>
    </Container>
  )
}